/**
 * Mock data for Trading Rules Dashboard
 * 
 * This file provides mock data matching the expected API response shapes
 * for the trading rules system. Used for UI development before backend is ready.
 */

import { RULE_TYPE_DEFINITIONS } from '../constants/ruleTypeDefinitions';

const emptyRuleDayBuckets = (dateStr) => {
  const d = { date: dateStr, timestamp: dateStr };
  Object.keys(RULE_TYPE_DEFINITIONS).forEach((k) => {
    d[k] = 0;
  });
  return d;
};

// Trading rules configuration for a user's account
export const mockTradingRulesConfig = {
  account_id: 'SIM123456',
  rule_thresholds: {
    max_trades_per_day: 8,
    max_loss_per_day: 500.00,
    profit_target_per_day: 100.00,
    consecutive_losses: 2,
    consecutive_loss_cooldown_minutes: 30,
    max_loss_per_position: 150.00,
    max_risk_per_trade: 100.00,
    trading_windows: [
      {
        startTime: '09:30:00',
        endTime: '15:30:00'
      }
    ],
    trading_window_timezone: 'America/New_York'
  },
  enforcement_config: {
    max_trades_per_day: { enabled: true },
    max_loss_per_day: { enabled: true },
    profit_target_per_day: { enabled: true },
    consecutive_loss_cooldown: { enabled: true },
    max_loss_per_position: { enabled: true },
    max_risk_per_trade: { enabled: true },
    trading_window: { enabled: true },
    stop_take_profit_modifications: { enabled: true },
    journal_tracking: { enabled: true }
  },
  additional_rules: {},
  created_at: '2026-01-15T10:23:45Z',
  updated_at: '2026-02-01T14:30:12Z'
};

// Today's metrics summary
export const mockTradingRulesMetrics = {
  date: '2026-02-03',
  account_id: 'SIM123456',
  metrics: {
    trades_taken: 5,
    max_trades: 8,
    trades_outside_window: 2,
    daily_loss_current: 245.50,
    daily_loss_limit: 500.00,
    daily_loss_breached: false,
    position_loss_events: 1,
    stop_modifications: 4,
    target_modifications: 2,
    total_trades: 10,
    modification_rate: 0.60, // (4 + 2) / 10
    total_violations: 7
  },
  journal_compliance: {
    journals_created: 5, // 5 journals created
    new_positions_opened: 5 // 5 new positions opened (matches trades_taken)
  },
  rules_configured: true,
  enforcement_status: {
    max_trades_per_day: false,
    max_loss_per_day: true,
    max_loss_per_position: false,
    trading_window: false
  }
};

// Recent violations for today
export const mockTradingRulesViolations = {
  violations: [
    {
      id: 1001,
      rule_type: 'outside_trading_window',
      violation_timestamp: '2026-02-03T16:45:23Z',
      order_id: 'ORD-12345',
      threshold_value: null,
      actual_value: null,
      violation_metadata: {
        window_start: '09:30',
        window_end: '15:30',
        trade_time: '16:45',
        timezone: 'America/New_York'
      },
      severity: 'medium'
    },
    {
      id: 1002,
      rule_type: 'stop_loss_modified',
      violation_timestamp: '2026-02-03T14:23:11Z',
      symbol: 'TSLA',
      order_id: 'ORD-11223',
      position_id: 'POS-445',
      threshold_value: null,
      actual_value: null,
      violation_metadata: {
        original_stop: 245.50,
        new_stop: 242.00,
        modification_type: 'moved_closer',
        price_at_time: 247.30
      },
      severity: 'medium'
    },
    {
      id: 1003,
      rule_type: 'stop_loss_modified',
      violation_timestamp: '2026-02-03T13:15:44Z',
      symbol: 'AAPL',
      order_id: 'ORD-11156',
      position_id: 'POS-442',
      threshold_value: null,
      actual_value: null,
      violation_metadata: {
        original_stop: 178.25,
        new_stop: 176.50,
        modification_type: 'widened_stop',
        price_at_time: 179.80
      },
      severity: 'high'
    },
    {
      id: 1004,
      rule_type: 'profit_target_modified',
      violation_timestamp: '2026-02-03T11:48:22Z',
      symbol: 'NVDA',
      order_id: 'ORD-11089',
      position_id: 'POS-438',
      threshold_value: null,
      actual_value: null,
      violation_metadata: {
        original_target: 892.00,
        new_target: 887.50,
        modification_type: 'moved_closer',
        price_at_time: 885.20
      },
      severity: 'medium'
    },
    {
      id: 1005,
      rule_type: 'outside_trading_window',
      violation_timestamp: '2026-02-03T08:15:10Z',
      order_id: 'ORD-10923',
      threshold_value: null,
      actual_value: null,
      violation_metadata: {
        window_start: '09:30',
        window_end: '15:30',
        trade_time: '08:15',
        timezone: 'America/New_York'
      },
      severity: 'medium'
    },
    {
      id: 1006,
      rule_type: 'position_loss_exceeded',
      violation_timestamp: '2026-02-03T14:55:30Z',
      symbol: 'MSFT',
      position_id: 'POS-449',
      threshold_value: 150.00,
      actual_value: 167.50,
      violation_metadata: {
        breach_amount: 17.50,
        entry_price: 412.30,
        current_price: 408.95
      },
      severity: 'high'
    },
    {
      id: 1007,
      rule_type: 'profit_target_modified',
      violation_timestamp: '2026-02-03T10:22:15Z',
      symbol: 'SPY',
      order_id: 'ORD-10856',
      position_id: 'POS-435',
      threshold_value: null,
      actual_value: null,
      violation_metadata: {
        original_target: 498.50,
        new_target: 500.25,
        modification_type: 'moved_away',
        price_at_time: 497.85
      },
      severity: 'low'
    },
    {
      id: 1008,
      rule_type: 'max_risk_per_trade',
      violation_timestamp: '2026-02-03T11:30:45Z',
      symbol: 'AAPL',
      order_id: 'ORD-10987',
      threshold_value: 100.00,
      actual_value: 125.50,
      violation_metadata: {
        entry_price: 175.50,
        stop_loss_price: 173.00,
        quantity: 50,
        calculated_risk: 125.50,
        threshold: 100.00
      },
      severity: 'warning'
    }
  ],
  total_count: 8,
  violations_by_type: {
    outside_trading_window: 2,
    stop_loss_modified: 2,
    profit_target_modified: 2,
    position_loss_exceeded: 1,
    max_risk_per_trade: 1
  }
};

// v=2 / warning scenario: table rows matching mockScenarios.warning.violations_by_type (timestamps spread across multiple days)
export const mockWarningViolations = [
  {
    id: 2101,
    rule_type: 'outside_trading_window',
    violation_timestamp: '2026-01-28T14:20:00Z',
    order_id: 'ORD-W01',
    threshold_value: null,
    actual_value: null,
    violation_metadata: { window_start: '09:30', window_end: '15:30', trade_time: '09:20', timezone: 'America/New_York' },
    severity: 'medium'
  },
  {
    id: 2102,
    rule_type: 'stop_loss_modified',
    violation_timestamp: '2026-01-28T15:45:00Z',
    symbol: 'QQQ',
    order_id: 'ORD-W02',
    position_id: 'POS-W01',
    threshold_value: null,
    actual_value: null,
    violation_metadata: { original_stop: 520.0, new_stop: 518.5, modification_type: 'moved_closer', price_at_time: 521.2 },
    severity: 'medium'
  },
  {
    id: 2103,
    rule_type: 'max_trades_per_day',
    violation_timestamp: '2026-01-29T16:10:00Z',
    order_id: 'ORD-W03',
    threshold_value: 8,
    actual_value: 9,
    violation_metadata: {
      trade_count: 9,
      after_limit_pnls: [-62.5, 18.0],
    },
    severity: 'medium'
  },
  {
    id: 2104,
    rule_type: 'position_loss_exceeded',
    violation_timestamp: '2026-01-29T17:22:00Z',
    symbol: 'TSLA',
    position_id: 'POS-W02',
    threshold_value: 150.0,
    actual_value: 162.0,
    violation_metadata: { breach_amount: 12.0, entry_price: 248.0, current_price: 244.76 },
    severity: 'high'
  },
  {
    id: 2105,
    rule_type: 'profit_target_modified',
    violation_timestamp: '2026-01-29T18:05:00Z',
    symbol: 'NVDA',
    order_id: 'ORD-W04',
    position_id: 'POS-W03',
    threshold_value: null,
    actual_value: null,
    violation_metadata: { original_target: 910.0, new_target: 905.0, modification_type: 'moved_closer', price_at_time: 908.0 },
    severity: 'low'
  },
  {
    id: 2106,
    rule_type: 'outside_trading_window',
    violation_timestamp: '2026-01-30T13:40:00Z',
    order_id: 'ORD-W05',
    threshold_value: null,
    actual_value: null,
    violation_metadata: { window_start: '09:30', window_end: '15:30', trade_time: '08:40', timezone: 'America/New_York' },
    severity: 'medium'
  },
  {
    id: 2107,
    rule_type: 'stop_loss_modified',
    violation_timestamp: '2026-01-30T14:15:00Z',
    symbol: 'AAPL',
    order_id: 'ORD-W06',
    position_id: 'POS-W04',
    threshold_value: null,
    actual_value: null,
    violation_metadata: { original_stop: 178.0, new_stop: 176.0, modification_type: 'widened_stop', price_at_time: 179.2 },
    severity: 'high'
  },
  {
    id: 2108,
    rule_type: 'max_risk_per_trade',
    violation_timestamp: '2026-01-30T15:30:00Z',
    symbol: 'META',
    order_id: 'ORD-W07',
    threshold_value: 100.0,
    actual_value: 118.0,
    violation_metadata: { entry_price: 620.0, stop_loss_price: 612.0, quantity: 15, calculated_risk: 118.0, threshold: 100.0 },
    severity: 'warning'
  },
  {
    id: 2109,
    rule_type: 'outside_trading_window',
    violation_timestamp: '2026-01-31T12:00:00Z',
    order_id: 'ORD-W08',
    threshold_value: null,
    actual_value: null,
    violation_metadata: { window_start: '09:30', window_end: '15:30', trade_time: '07:00', timezone: 'America/New_York' },
    severity: 'medium'
  },
  {
    id: 2110,
    rule_type: 'position_loss_exceeded',
    violation_timestamp: '2026-01-31T12:45:00Z',
    symbol: 'AMD',
    position_id: 'POS-W05',
    threshold_value: 150.0,
    actual_value: 155.25,
    violation_metadata: { breach_amount: 5.25, entry_price: 122.0, current_price: 120.95 },
    severity: 'high'
  },
  {
    id: 2111,
    rule_type: 'stop_loss_modified',
    violation_timestamp: '2026-01-31T12:50:00Z',
    symbol: 'SPY',
    order_id: 'ORD-W09',
    position_id: 'POS-W06',
    threshold_value: null,
    actual_value: null,
    violation_metadata: { original_stop: 598.0, new_stop: 596.0, modification_type: 'moved_closer', price_at_time: 599.0 },
    severity: 'medium'
  },
  {
    id: 2112,
    rule_type: 'outside_trading_window',
    violation_timestamp: '2026-02-01T14:10:00Z',
    order_id: 'ORD-W10',
    threshold_value: null,
    actual_value: null,
    violation_metadata: { window_start: '09:30', window_end: '15:30', trade_time: '09:10', timezone: 'America/New_York' },
    severity: 'medium'
  },
  {
    id: 2113,
    rule_type: 'profit_target_modified',
    violation_timestamp: '2026-02-01T15:00:00Z',
    symbol: 'GOOGL',
    order_id: 'ORD-W11',
    position_id: 'POS-W07',
    threshold_value: null,
    actual_value: null,
    violation_metadata: { original_target: 192.0, new_target: 191.0, modification_type: 'moved_closer', price_at_time: 191.5 },
    severity: 'low'
  },
  {
    id: 2114,
    rule_type: 'max_trades_per_day',
    violation_timestamp: '2026-02-02T14:00:00Z',
    order_id: 'ORD-W12',
    threshold_value: 8,
    actual_value: 9,
    violation_metadata: {
      trade_count: 9,
      after_limit_pnls: [-95.25, -40.0, 22.5],
    },
    severity: 'medium'
  },
  {
    id: 2115,
    rule_type: 'daily_loss_exceeded',
    violation_timestamp: '2026-02-02T15:30:00Z',
    order_id: 'ORD-W13',
    threshold_value: 500.0,
    actual_value: 512.0,
    violation_metadata: { session_pnl: -512.0, limit: 500.0 },
    severity: 'high'
  },
  {
    id: 2116,
    rule_type: 'max_risk_per_trade',
    violation_timestamp: '2026-02-02T14:45:00Z',
    symbol: 'TSLA',
    order_id: 'ORD-W14',
    threshold_value: 100.0,
    actual_value: 105.0,
    violation_metadata: { entry_price: 355.0, stop_loss_price: 348.0, quantity: 15, calculated_risk: 105.0, threshold: 100.0 },
    severity: 'warning'
  },
  {
    id: 2117,
    rule_type: 'stop_loss_modified',
    violation_timestamp: '2026-02-03T10:22:00Z',
    symbol: 'MSFT',
    order_id: 'ORD-W15',
    position_id: 'POS-W08',
    threshold_value: null,
    actual_value: null,
    violation_metadata: { original_stop: 410.0, new_stop: 408.0, modification_type: 'moved_closer', price_at_time: 411.0 },
    severity: 'medium'
  },
  {
    id: 2118,
    rule_type: 'profit_target_modified',
    violation_timestamp: '2026-02-03T11:48:00Z',
    symbol: 'SPY',
    order_id: 'ORD-W16',
    position_id: 'POS-W09',
    threshold_value: null,
    actual_value: null,
    violation_metadata: { original_target: 600.0, new_target: 602.0, modification_type: 'moved_away', price_at_time: 599.0 },
    severity: 'low'
  },
  {
    id: 2119,
    rule_type: 'stop_loss_modified',
    violation_timestamp: '2026-02-03T13:15:00Z',
    symbol: 'AAPL',
    order_id: 'ORD-W17',
    position_id: 'POS-W10',
    threshold_value: null,
    actual_value: null,
    violation_metadata: { original_stop: 230.0, new_stop: 228.5, modification_type: 'widened_stop', price_at_time: 231.0 },
    severity: 'medium'
  },
  {
    id: 2120,
    rule_type: 'trade_without_stop_loss',
    violation_timestamp: '2026-01-30T15:55:00Z',
    symbol: 'AMD',
    order_id: 'ORD-W20',
    position_id: 'POS-W20',
    threshold_value: null,
    actual_value: 'No stop',
    violation_metadata: { realized_pnl: -185.5, quantity: 40 },
    severity: 'high'
  },
  {
    id: 2121,
    rule_type: 'trade_without_stop_loss',
    violation_timestamp: '2026-02-01T16:20:00Z',
    symbol: 'NVDA',
    order_id: 'ORD-W21',
    position_id: 'POS-W21',
    threshold_value: null,
    actual_value: 'No stop',
    violation_metadata: { realized_pnl: -92.25, quantity: 5 },
    severity: 'high'
  },
  {
    id: 2122,
    rule_type: 'trade_without_stop_loss',
    violation_timestamp: '2026-02-02T17:05:00Z',
    symbol: 'TSLA',
    order_id: 'ORD-W22',
    position_id: 'POS-W22',
    threshold_value: null,
    actual_value: 'Stop Added',
    violation_metadata: {
      realized_pnl: -40.0,
      stop_loss_added_after_open: true,
      quantity: 10,
    },
    severity: 'medium'
  }
];

// Historical daily loss limit breaches (last 30 days)
// Structure matches backend API response from /trading_rules_breaches
// Note: loss_limit is stored as negative (e.g., -500 means limit of $500)
// end_of_day_loss and max_drawdown are the actual P&L values (negative = loss)
// breach_amount is how much over the limit (negative = exceeded by this amount)
export const mockDailyLossBreaches = [
  // Example: Feb 21, 2026 - Breached at -$500, recovered to +$143 by end of day
  {
    breach_date: '2026-02-21',
    loss_limit: -500.00, // Limit is $500
    breach_amount: 0.00, // Exactly at the limit when detected
    max_drawdown: -500.00, // Loss when detected: -$500
    max_drawdown_timestamp: '2026-02-21T13:35:31Z', // 9:35:31 AM EDT (13:35:31 UTC, EDT is UTC-4)
    end_of_day_loss: 143.00, // Recovered to +$143 by end of day
    breach_timestamp: '2026-02-21T13:35:31Z', // 9:35:31 AM
    final_breach_timestamp: '2026-02-21T21:00:00Z',
    recovered: true, // Recovered above threshold (positive P&L)
    is_pending: false
  },
  // Example: Started at $0, limit is $500, exceeded by losing -$500 (total loss: -$500)
  {
    breach_date: '2026-01-28',
    loss_limit: -500.00, // Limit is $500 (stored as negative)
    breach_amount: 0.00, // Exactly at the limit (lost exactly $500, limit is $500)
    max_drawdown: -654.00, // Worst P&L: lost $500 (started at $0, lost $500)
    max_drawdown_timestamp: '2026-01-28T14:23:11Z',
    end_of_day_loss: -543.00, // Final P&L: -$543 (lost $543 total)
    breach_timestamp: '2026-01-28T14:15:30Z',
    final_breach_timestamp: '2026-01-28T16:00:00Z',
    recovered: false,
    is_pending: false
  },
  // Example: Started at $0, limit is $500, exceeded by losing -$605 (exceeded by -$105)
  {
    breach_date: '2026-01-15',
    loss_limit: -500.00,
    breach_amount: -105.22, // Exceeded limit by $105.22
    max_drawdown: -605.22, // Worst P&L: lost $605.22
    max_drawdown_timestamp: '2026-01-15T15:45:22Z',
    end_of_day_loss: -605.22, // Final P&L: -$605.22
    breach_timestamp: '2026-01-15T15:30:15Z',
    final_breach_timestamp: '2026-01-15T16:00:00Z',
    recovered: false,
    is_pending: false
  },
  {
    breach_date: '2026-01-15',
    loss_limit: -500.00,
    breach_amount: -105.22,
    max_drawdown: -605.22,
    max_drawdown_timestamp: '2026-01-15T15:45:22Z',
    end_of_day_loss: -605.22,
    breach_timestamp: '2026-01-15T15:30:15Z',
    final_breach_timestamp: '2026-01-15T16:00:00Z',
    recovered: false,
    is_pending: false
  },
  // Example: Started at $0, limit is $500, exceeded but recovered to -$450 (recovered above threshold)
  {
    breach_date: '2026-01-08',
    loss_limit: -500.00,
    breach_amount: -67.89, // Exceeded by $67.89 at worst point
    max_drawdown: -567.89, // Worst P&L: lost $567.89
    max_drawdown_timestamp: '2026-01-08T13:20:45Z',
    end_of_day_loss: -450.00, // Recovered to -$450 by end of day (above -$500 threshold)
    breach_timestamp: '2026-01-08T13:10:20Z',
    final_breach_timestamp: '2026-01-08T16:00:00Z',
    recovered: true, // Recovered above threshold by EOD
    is_pending: false
  },
  // Example: Started at $0, limit is $500, exceeded by losing -$545, recovered to -$485 (still breached)
  {
    breach_date: '2026-01-05',
    loss_limit: -500.00,
    breach_amount: -45.30, // Exceeded by $45.30 at worst point
    max_drawdown: -545.30, // Worst P&L: lost $545.30
    max_drawdown_timestamp: '2026-01-05T11:30:00Z',
    end_of_day_loss: -485.00, // Recovered to -$485 but still below -$500 threshold
    breach_timestamp: '2026-01-05T11:15:10Z',
    final_breach_timestamp: '2026-01-05T16:00:00Z',
    recovered: false, // Still below threshold at EOD
    is_pending: false
  }
];

// Example: Metrics for different time ranges
export const mockMetricsByTimeRange = {
  today: mockTradingRulesMetrics,
  last7days: {
    date_range: '2026-01-27 to 2026-02-03',
    account_id: 'SIM123456',
    metrics: {
      trades_taken: 32,
      max_trades: 8, // per day
      trades_outside_window: 8,
      daily_loss_current: -125.75, // Week total
      daily_loss_limit: 500.00,
      daily_loss_breached: true, // At least one day breached
      position_loss_events: 5,
      stop_modifications: 18,
      target_modifications: 12,
      total_trades: 50,
      modification_rate: 0.60,
      total_violations: 38
    },
    journal_compliance: {
      journals_created: 25, // 25 journals created out of 32 trades (78% compliance)
      new_positions_opened: 32 // 32 new positions opened
    },
    rules_configured: true,
    enforcement_status: {
      max_trades_per_day: false,
      max_loss_per_day: true,
      max_loss_per_position: false,
      trading_window: false
    }
  },
  last30days: {
    date_range: '2026-01-04 to 2026-02-03',
    account_id: 'SIM123456',
    metrics: {
      trades_taken: 145,
      max_trades: 8,
      trades_outside_window: 24,
      daily_loss_current: 892.50, // Month total profit
      daily_loss_limit: 500.00,
      daily_loss_breached: true, // Had 3 breach days
      position_loss_events: 18,
      stop_modifications: 67,
      target_modifications: 43,
      total_trades: 180,
      modification_rate: 0.61,
      total_violations: 152
    },
    journal_compliance: {
      journals_created: 120, // 120 journals created out of 145 trades (83% compliance)
      new_positions_opened: 145 // 145 new positions opened
    },
    rules_configured: true,
    enforcement_status: {
      max_trades_per_day: false,
      max_loss_per_day: true,
      max_loss_per_position: false,
      trading_window: false
    }
  }
};

// Three test scenarios for different states
export const mockScenarios = {
  // Scenario 1: Clean Slate (No violations, healthy state)
  healthy: {
    date: '2026-02-03',
    account_id: 'SIM123456',
    metrics: {
      trades_taken: 0,
      max_trades: 8,
      trades_outside_window: 0,
      daily_loss_current: 0,
      daily_loss_limit: 500.00,
      daily_loss_breached: false,
      position_loss_events: 0,
      stop_modifications: 0,
      target_modifications: 0,
      total_trades: 0,
      modification_rate: 0,
      total_violations: 0
    },
    journal_compliance: {
      journals_created: 0, // No journals created
      new_positions_opened: 0 // No trades
    },
    rules_configured: true,
    enforcement_status: {
      max_trades_per_day: false,
      max_loss_per_day: true,
      max_loss_per_position: false,
      trading_window: false
    },
    violations_by_type: {}
  },

  // Scenario 2: Doing Decently (Some violations but not hitting limits) — v=2 / v=warning
  warning: {
    date: '2026-02-03',
    account_id: 'SIM123456',
    metrics: {
      // Under max_trades (8): do not imply a trade-count breach in headline metrics
      trades_taken: 6,
      max_trades: 8,
      trades_outside_window: 4,
      daily_loss_current: 185.50,
      daily_loss_limit: 500.00,
      daily_loss_breached: false,
      position_loss_events: 2,
      stop_modifications: 9,
      target_modifications: 5,
      total_trades: 42,
      modification_rate: 0.33,
      total_violations: 21
    },
    journal_compliance: {
      journals_created: 10,
      new_positions_opened: 6
    },
    rules_configured: true,
    enforcement_status: {
      max_trades_per_day: false,
      max_loss_per_day: true,
      max_loss_per_position: false,
      trading_window: false
    },
    violations_by_type: {
      max_trades_per_day: 2,
      outside_trading_window: 4,
      daily_loss_exceeded: 1,
      position_loss_exceeded: 2,
      stop_loss_modified: 5,
      profit_target_modified: 3,
      max_risk_per_trade: 2,
      trade_without_stop_loss: 2
    }
  },

  // Scenario 3: Extreme (Critical State)
  // Started at $0, limit is $500, exceeded by losing -$856 (exceeded by $356)
  extreme: {
    date: '2026-02-03',
    account_id: 'SIM123456',
    metrics: {
      trades_taken: 12,
      max_trades: 8,
      trades_outside_window: 5,
      daily_loss_current: 856.00, // Lost $856 (exceeded $500 limit by $356)
      daily_loss_limit: 500.00,
      daily_loss_breached: true,
      position_loss_events: 3,
      stop_modifications: 20, // 20 stop modifications
      target_modifications: 14, // 14 target modifications (total: 34 modifications)
      total_trades: 51, // 51 total trades (34/51 = 67% modification rate)
      modification_rate: 0.67, // 34/51 = 0.67
      total_violations: 18
    },
    journal_compliance: {
      journals_created: 32, // 32 journals created out of 51 trades (63% compliance)
      new_positions_opened: 51 // 51 new positions opened (matches total_trades)
    },
    rules_configured: true,
    enforcement_status: {
      max_trades_per_day: false,
      max_loss_per_day: true,
      max_loss_per_position: false,
      trading_window: false
    },
    violations_by_type: {
      max_trades_per_day: 4,
      outside_trading_window: 5,
      daily_loss_exceeded: 1,
      stop_loss_modified: 5,
      profit_target_modified: 2,
      position_loss_exceeded: 1,
      max_risk_per_trade: 3
    }
  }
};

// Helper function to get metrics by time range
export const getMockMetricsByTimeRange = (timeRange) => {
  switch (timeRange) {
    case 'today':
      return mockTradingRulesMetrics;
    case '7days':
      return mockMetricsByTimeRange.last7days;
    case '30days':
      return mockMetricsByTimeRange.last30days;
    default:
      return mockTradingRulesMetrics;
  }
};

// Helper function to get scenario by version param
export const getMockScenario = (version) => {
  switch (version) {
    case 'healthy':
    case '1':
      return mockScenarios.healthy;
    case 'warning':
    case '2':
      return mockScenarios.warning;
    case 'extreme':
    case '3':
      return mockScenarios.extreme;
    default:
      return mockTradingRulesMetrics; // Default to original
  }
};

// Helper function to get breaches for a scenario
// For extreme scenario, includes today's pending breach
export const getMockBreachesForScenario = (scenarioVersion) => {
  if (scenarioVersion === '1' || scenarioVersion === 'healthy') {
    return []; // No breaches for healthy scenario
  }
  
  // For extreme scenario, add today's pending breach
  // Example: Started at $0, limit is $500, currently exceeded by losing -$500
  if (scenarioVersion === '3' || scenarioVersion === 'extreme') {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    return [
      {
        breach_date: today,
        loss_limit: -500.00, // Limit is $500
        breach_amount: 0.00, // Exactly at limit (lost $500, limit is $500)
        max_drawdown: -500.00, // Current worst P&L: -$500 (started at $0, lost $500)
        max_drawdown_timestamp: new Date().toISOString(),
        end_of_day_loss: null, // Not finalized yet
        breach_timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 minutes ago
        final_breach_timestamp: null,
        recovered: false,
        is_pending: true
      },
      ...mockDailyLossBreaches
    ];
  }
  
  // For warning and default scenarios, return historical breaches
  return mockDailyLossBreaches;
};

/** v=2 only: intraday violation mix (no max-trades breach — those are on prior days in the period mock). */
export const getMockWarningViolationsByTypeToday = () => ({
  stop_loss_modified: 2,
  profit_target_modified: 1
});

/** Three violation rows with timestamps on the current UTC day (for `timeRange === 'today'`). */
export const buildMockWarningViolationsToday = () => {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  const base = `${y}-${m}-${d}`;
  return [
    {
      id: 2117,
      rule_type: 'stop_loss_modified',
      violation_timestamp: `${base}T10:22:00Z`,
      symbol: 'MSFT',
      order_id: 'ORD-W15',
      position_id: 'POS-W08',
      threshold_value: null,
      actual_value: null,
      violation_metadata: {
        original_stop: 410.0,
        new_stop: 408.0,
        modification_type: 'moved_closer',
        price_at_time: 411.0
      },
      severity: 'medium'
    },
    {
      id: 2118,
      rule_type: 'profit_target_modified',
      violation_timestamp: `${base}T11:48:00Z`,
      symbol: 'SPY',
      order_id: 'ORD-W16',
      position_id: 'POS-W09',
      threshold_value: null,
      actual_value: null,
      violation_metadata: {
        original_target: 600.0,
        new_target: 602.0,
        modification_type: 'moved_away',
        price_at_time: 599.0
      },
      severity: 'low'
    },
    {
      id: 2119,
      rule_type: 'stop_loss_modified',
      violation_timestamp: `${base}T13:15:00Z`,
      symbol: 'AAPL',
      order_id: 'ORD-W17',
      position_id: 'POS-W10',
      threshold_value: null,
      actual_value: null,
      violation_metadata: {
        original_stop: 230.0,
        new_stop: 228.5,
        modification_type: 'widened_stop',
        price_at_time: 231.0
      },
      severity: 'medium'
    }
  ];
};

/**
 * Metrics + breakdown for warning scenario when viewing Today only (aligned with trades under cap + intraday chart).
 */
export const getMockWarningTodayScenarioPatch = (baseScenario) => ({
  ...baseScenario,
  metrics: {
    ...baseScenario.metrics,
    trades_taken: 6,
    max_trades: 8,
    total_violations: 3,
    stop_modifications: 4,
    target_modifications: 2,
    total_trades: 14,
    modification_rate: 4 / 14,
    trades_outside_window: 0,
    position_loss_events: 0
  },
  violations_by_type: getMockWarningViolationsByTypeToday(),
  journal_compliance: {
    journals_created: 5,
    new_positions_opened: 6
  }
});

/** Violation list for mock mode (`?v=`) — `timeRange` selects period list vs today's rows for v=2 */
export const getMockViolationsForScenario = (scenarioVersion, timeRange = '7days') => {
  if (scenarioVersion === '1' || scenarioVersion === 'healthy') {
    return [];
  }
  if (scenarioVersion === '2' || scenarioVersion === 'warning') {
    if (timeRange === 'today') {
      return buildMockWarningViolationsToday();
    }
    return mockWarningViolations;
  }
  if (scenarioVersion === '3' || scenarioVersion === 'extreme') {
    return mockTradingRulesViolations.violations;
  }
  return mockTradingRulesViolations.violations;
};

// 7-day warning (v=2): every day has at least one violation; types sum to mockScenarios.warning.violations_by_type
const WARNING_7D_DAY_COUNTS = [
  { outside_trading_window: 1, stop_loss_modified: 1 },
  { max_trades_per_day: 1, position_loss_exceeded: 1, profit_target_modified: 1 },
  { outside_trading_window: 1, stop_loss_modified: 1, max_risk_per_trade: 1, trade_without_stop_loss: 1 },
  { outside_trading_window: 1, position_loss_exceeded: 1, stop_loss_modified: 1 },
  { outside_trading_window: 1, profit_target_modified: 1, stop_loss_modified: 1, trade_without_stop_loss: 1 },
  { max_trades_per_day: 1, daily_loss_exceeded: 1, max_risk_per_trade: 1 },
  { stop_loss_modified: 1, profit_target_modified: 1 }
];

// 30-day warning: same totals spread across 10 distinct days (rest are zeros)
const WARNING_30D_BY_DAY_INDEX = {
  29: { outside_trading_window: 1, stop_loss_modified: 1 },
  26: { position_loss_exceeded: 1, max_risk_per_trade: 1 },
  23: { outside_trading_window: 1, stop_loss_modified: 1, profit_target_modified: 1 },
  20: { outside_trading_window: 1, profit_target_modified: 1, stop_loss_modified: 1 },
  17: { outside_trading_window: 1, profit_target_modified: 1, trade_without_stop_loss: 1 },
  14: { max_trades_per_day: 1, daily_loss_exceeded: 1, max_risk_per_trade: 1 },
  11: { stop_loss_modified: 1, profit_target_modified: 1 },
  8: { max_trades_per_day: 1 },
  7: { position_loss_exceeded: 1, trade_without_stop_loss: 1 },
  5: { stop_loss_modified: 1 }
};

const buildWarningScenarioDailyData = (dayCount, today) => {
  const data = [];
  for (let i = dayCount - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setUTCDate(date.getUTCDate() - i);
    const dateStr = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}T00:00:00Z`;
    const day = emptyRuleDayBuckets(dateStr);

    if (dayCount === 7) {
      const pattern = WARNING_7D_DAY_COUNTS[dayCount - 1 - i];
      Object.assign(day, pattern);
    } else if (dayCount === 30) {
      const patch = WARNING_30D_BY_DAY_INDEX[i];
      if (patch) {
        Object.assign(day, patch);
      }
    }
    data.push(day);
  }
  return data;
};

// Generate mock violations over time data
// Uses current date so chart's fill logic (which iterates over real dates) can match the keys
export const getMockViolationsOverTime = (timeRange = 'today', scenarioVersion = null) => {
  const today = new Date();
  const data = [];
  
  // Deterministic hash function for consistent pseudo-random values
  const hash = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  };
  
  // Deterministic pseudo-random between 0 and 1 based on seed
  const seededRandom = (seed) => {
    return (hash(seed.toString()) % 1000) / 1000;
  };
  
  // Get scenario if provided
  let scenario = null;
  if (scenarioVersion) {
    scenario = getMockScenario(scenarioVersion);
  }
  
  // For "today" timeframe, generate hourly data
  // Chart expects date keys in format YYYY-MM-DDTHH:00:00Z (UTC hour boundaries)
  // Chart iterates from midnight UTC to current hour - we must overlap that range
  if (timeRange === 'today') {
    const currentHourUTC = today.getUTCHours();
    const startHourUTC = 0;
    const endHourUTC = Math.max(16, currentHourUTC); // At least through 4pm or current hour
    const totalHours = endHourUTC - startHourUTC + 1;
    
    if (scenario && scenario.violations_by_type) {
      const isWarningScenario =
        scenarioVersion === '2' || scenarioVersion === 'warning';
      // Period totals include max-trades on prior days; "today" chart should only show intraday mix
      const violationsByType = isWarningScenario
        ? { ...getMockWarningViolationsByTypeToday() }
        : { ...scenario.violations_by_type };
      const remainingViolations = { ...violationsByType };
      
      // Generate hourly data - use UTC hour boundaries to match chart's expected format
      for (let h = 0; h < totalHours; h++) {
        const hourUTC = startHourUTC + h;
        const dateTimeStr = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}-${String(today.getUTCDate()).padStart(2, '0')}T${String(hourUTC).padStart(2, '0')}:00:00Z`;
        
        const hourData = {
          date: dateTimeStr,
          max_trades_per_day: 0,
          outside_trading_window: 0,
          daily_loss_exceeded: 0,
          position_loss_exceeded: 0,
          stop_loss_modified: 0,
          profit_target_modified: 0
        };
        
        // Distribute violations across hours
        Object.keys(remainingViolations).forEach(ruleType => {
          if (remainingViolations[ruleType] > 0) {
            const seed = `${timeRange}-${scenarioVersion}-${ruleType}-${h}`;
            const avgPerHour = remainingViolations[ruleType] / totalHours;
            const hourSeed = seededRandom(seed);
            const variation = (hourSeed - 0.5) * 0.6; // -0.3 to +0.3 variation
            let count = Math.floor(avgPerHour * (1 + variation));
            count = Math.max(0, Math.min(count, remainingViolations[ruleType]));
            
            hourData[ruleType] = count;
            remainingViolations[ruleType] -= count;
          }
        });
        
        data.push(hourData);
      }
      
      // Distribute any remaining violations
      if (remainingViolations) {
        const hoursRemaining = totalHours;
        Object.keys(remainingViolations).forEach(ruleType => {
          if (remainingViolations[ruleType] > 0) {
            const toAdd = Math.ceil(remainingViolations[ruleType] / hoursRemaining);
            for (let h = 0; h < totalHours && remainingViolations[ruleType] > 0; h++) {
              const actualAdd = Math.min(toAdd, remainingViolations[ruleType]);
              data[h][ruleType] += actualAdd;
              remainingViolations[ruleType] -= actualAdd;
            }
          }
        });
      }
    } else {
      // Default hourly data - use UTC hour boundaries to match chart format
      for (let h = 0; h < totalHours; h++) {
        const hourUTC = startHourUTC + h;
        const dateTimeStr = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}-${String(today.getUTCDate()).padStart(2, '0')}T${String(hourUTC).padStart(2, '0')}:00:00Z`;
        
        const ruleTypes = [
          'max_trades_per_day',
          'outside_trading_window',
          'daily_loss_exceeded',
          'position_loss_exceeded',
          'stop_loss_modified',
          'profit_target_modified'
        ];
        
        const hourData = { date: dateTimeStr };
        ruleTypes.forEach((ruleType, idx) => {
          const seed = `${timeRange}-${ruleType}-${h}`;
          const randomValue = seededRandom(seed);
          const maxValue = idx === 0 ? 2 : idx === 1 ? 3 : idx === 2 ? 1 : idx === 3 ? 2 : idx === 4 ? 4 : 3;
          hourData[ruleType] = Math.floor(randomValue * maxValue);
        });
        
        data.push(hourData);
      }
    }
    
    return data;
  }
  
  // For 7days and 30days, generate daily data
  let days;
  switch (timeRange) {
    case '7days':
      days = 7;
      break;
    case '30days':
      days = 30;
      break;
    default:
      days = 30;
  }

  // v=2 / warning: fixed daily buckets so the chart shows multiple days with activity (not one spike day)
  const isWarningScenario =
    scenarioVersion === '2' || scenarioVersion === 'warning';
  if (isWarningScenario && (timeRange === '7days' || timeRange === '30days')) {
    return buildWarningScenarioDailyData(days, today);
  }

  // If scenario exists, use its violations_by_type to generate data
  if (scenario && scenario.violations_by_type) {
    const violationsByType = scenario.violations_by_type;
    
    // Pre-calculate distribution for each rule type to ensure full timeframe coverage
    const distributionPlan = {};
    Object.keys(violationsByType).forEach(ruleType => {
      const total = violationsByType[ruleType];
      const avgPerDay = total / days;
      
      // For each rule type, determine which days get violations
      if (avgPerDay < 1) {
        // Sparse violations: distribute evenly across timeframe
        const violationDays = Math.min(total, days);
        const step = days / violationDays;
        const dayIndices = [];
        
        for (let v = 0; v < violationDays; v++) {
          const dayIndex = Math.floor(v * step + (step / 2)); // Center in each segment
          dayIndices.push(Math.min(dayIndex, days - 1));
        }
        
        distributionPlan[ruleType] = { type: 'sparse', dayIndices, total };
      } else {
        // Dense violations: use proportional distribution
        distributionPlan[ruleType] = { type: 'dense', total };
      }
    });
    
    // Generate data for each day - chart expects YYYY-MM-DDT00:00:00Z format
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setUTCDate(date.getUTCDate() - i);
      const dateStr = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}T00:00:00Z`;
      
      const dayOfWeek = date.getUTCDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      
      const dayData = {
        date: dateStr,
        timestamp: dateStr,
        max_trades_per_day: 0,
        outside_trading_window: 0,
        daily_loss_exceeded: 0,
        position_loss_exceeded: 0,
        stop_loss_modified: 0,
        profit_target_modified: 0
      };
      
      const isExtremeScenario = scenarioVersion === '3' || scenarioVersion === 'extreme';
      
      // Distribute violations based on plan
      Object.keys(distributionPlan).forEach(ruleType => {
        const plan = distributionPlan[ruleType];
        const seed = `${timeRange}-${scenarioVersion}-${ruleType}-${i}`;
        const daySeed = seededRandom(seed);
        
        let count = 0;
        
        if (plan.type === 'sparse') {
          // Sparse: this day gets a violation if it's in the planned day indices
          const dayIndexInArray = plan.dayIndices.indexOf(i);
          if (dayIndexInArray !== -1) {
            count = 1;
          }
        } else {
          // Dense: proportional distribution
          const avgPerDay = plan.total / days;
          const daysRemaining = days - i;
          const remainingTotal = plan.total - (data.reduce((sum, d) => sum + (d[ruleType] || 0), 0));
          
          if (remainingTotal > 0) {
            if (isExtremeScenario) {
              const weekendMultiplier = isWeekend ? 0.6 : 1.0;
              const variation = (daySeed - 0.5) * 0.3;
              count = Math.floor(avgPerDay * (1 + variation) * weekendMultiplier);
            } else {
              const weekendMultiplier = isWeekend ? 0.3 : 1.0;
              const weight = 1.0 - (i / days) * 0.2;
              const variation = (daySeed - 0.5) * 0.6;
              count = Math.floor(avgPerDay * weight * (1 + variation) * weekendMultiplier);
            }
            count = Math.max(0, Math.min(count, remainingTotal));
          }
        }
        
        dayData[ruleType] = count;
      });
      
      data.push(dayData);
    }
    
    // Distribute any remaining violations for dense types
    Object.keys(distributionPlan).forEach(ruleType => {
      const plan = distributionPlan[ruleType];
      if (plan.type === 'dense') {
        const currentTotal = data.reduce((sum, d) => sum + (d[ruleType] || 0), 0);
        const remaining = plan.total - currentTotal;
        
        if (remaining > 0) {
          const perDay = Math.floor(remaining / days);
          const remainder = remaining % days;
          
          for (let i = 0; i < days; i++) {
            let toAdd = perDay;
            if (i < remainder) {
              toAdd += 1;
            }
            data[i][ruleType] += toAdd;
          }
        }
      }
    });
  } else {
    // Default: Generate realistic violation counts deterministically
    // Chart expects YYYY-MM-DDT00:00:00Z format for 7days/30days
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setUTCDate(date.getUTCDate() - i);
      const dateStr = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}T00:00:00Z`;
      
      const dayOfWeek = date.getUTCDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      
      // Less violations on weekends
      const baseMultiplier = isWeekend ? 0.3 : 1;
      
      // Use deterministic seeds for each rule type and day
      const ruleTypes = [
        'max_trades_per_day',
        'outside_trading_window',
        'daily_loss_exceeded',
        'position_loss_exceeded',
        'stop_loss_modified',
        'profit_target_modified'
      ];
      
      const dayData = { date: dateStr, timestamp: dateStr };
      ruleTypes.forEach((ruleType, idx) => {
        const seed = `${timeRange}-${ruleType}-${i}`;
        const randomValue = seededRandom(seed);
        const maxValue = idx === 0 ? 2 : idx === 1 ? 3 : idx === 2 ? 1 : idx === 3 ? 2 : idx === 4 ? 4 : 3;
        dayData[ruleType] = Math.floor(randomValue * maxValue * baseMultiplier);
      });
      
      data.push(dayData);
    }
  }
  
  return data;
};
